/**
 * "No match — create it": the inline organisation-create affordance inside the governed ref picker.
 *
 * WHY IT IS HERE AND NOT A DEAD END. The CRM mints organisations only from ingested mail, so the
 * picker can only ever offer a company that has emailed us — and the moment of need is exactly the
 * moment a company has not. Orange Flood Control is the target of a deal in exclusivity and has never
 * sent us a message, so naming it as the target was impossible from this flow. This turns the picker's
 * "No matches." from a full stop into the one action that resolves it, and then SELECTS the new
 * company so the fact-add continues without searching again.
 *
 * WHY IT LIVES IN THE PICKER'S OWN MODULE. `CrmFactEditModal` takes the picker as a render prop
 * precisely so it owns no write path and stays testable without a QueryClient. Putting a create
 * mutation in the modal would couple it to a write it deliberately does not own, so the affordance
 * belongs to the picker side of that seam — this component, mounted by `RefCellEditor` and opt-in per
 * host (`allowCreate`), so the grid's cell editors are untouched.
 *
 * ⚠ SEARCH BEFORE CREATE, ENFORCED BY THE FORM'S SHAPE, not by a warning. Step 1 ("Check for
 * duplicates") calls the endpoint with NO confirmation and shows what already looks like this
 * company; only then does step 2 ("Yes — create it") appear. This is the same rule the backend
 * enforces, and it exists because the surrounding flow already carries the adjacent hazard: an
 * operator sent back to search "could pick a DIFFERENT company under a heading naming this one"
 * (CrmFactEditModal §Add). A create button that skipped the near-match step would be that trap
 * repainted — and typing companies in by hand is how the next duplicate gets made, while this
 * programme is simultaneously trying to remove three of them (Santander, Ramboll, Grant Thornton).
 *
 * ⚠ ADMIN-GATED ON THE REAL PRINCIPAL. The endpoint is `require_admin`, whose gate reads
 * `actual_email`, so this reads `realEmail` and NOT `effectiveEmail`: a view-as session must not be
 * shown a control the server will refuse. Whether a non-admin deal team should be able to create a
 * counterparty is an owner ruling, not something to widen here.
 */
import { useState } from 'react';
import { isAdmin } from '../../auth/admins';
import { useEffectiveUser } from '../../auth/useAuthUser';
import { useCreateOrganization } from '../../hooks/useCrm';
import type { CrmOrgCreateResponse, CrmOrgDomainConflict, CrmOrgNearMatch } from '../../types';
import styles from './crm.module.css';

/** How a near-match matched, in the operator's language. A shared domain is identity; a shared name is
 *  only a resemblance, and the wording has to keep those apart or every row reads equally alarming. */
const BASIS_LABEL: Record<CrmOrgNearMatch['basis'], string> = {
  domain: 'same web domain',
  domain_alias: 'a domain this record still answers to',
  name_exact: 'same name',
  name_contains: 'similar name',
};

/** Where the other company keeps the domain. Mirrors the backend's `_SLOT_PHRASE` — a domain belongs
 *  to one company whichever slot either side keeps it in, and only the `primary` case is the one the
 *  unique index refuses, so naming the slot is what makes the refusal actionable. */
const SLOT_PHRASE: Record<CrmOrgDomainConflict['holder_slot'], string> = {
  primary: 'as its primary domain',
  alt: 'as one of its alternate domains',
  alias: 'as a domain alias it still resolves by',
};

/** A record in a state no other surface shows. Saying so is the whole point: "that domain is taken"
 *  against a company nobody can find is otherwise baffling. */
function stateNote(m: CrmOrgNearMatch): string | null {
  if (m.state === 'merged_away') {
    return `merged away into #${m.merged_into_id ?? '?'} — use that record`;
  }
  if (m.state === 'soft_deleted') return 'deleted, but still holds its domain';
  return null;
}

interface Props {
  /** What the operator typed into the picker's search box — the name the create is pre-filled with. */
  typed: string;
  /** Selects the created company in the picker, so the fact-add continues without a second search. */
  onCreated: (entity: { id: number; name: string }) => void;
  onCancel: () => void;
}

export function CrmOrgCreateInline({ typed, onCreated, onCancel }: Props) {
  const { realEmail } = useEffectiveUser();
  const [name, setName] = useState(typed.trim());
  const [domain, setDomain] = useState('');
  // The near-match report from step 1. Held in state rather than read off the mutation so a failed
  // step 2 (a claimed domain) does not blank the list the operator is reading.
  const [checked, setChecked] = useState<CrmOrgCreateResponse | null>(null);
  const create = useCreateOrganization();

  // A control the server would refuse is worse than no control (see the header note on the real
  // principal). Render nothing rather than a button that 403s.
  if (!isAdmin(realEmail)) return null;

  const trimmed = name.trim();
  // Any edit to the name or domain INVALIDATES the check — the near-matches on screen were computed
  // for different input, and confirming against a stale list is precisely the duplicate this guards.
  const stale = checked !== null && (checked.name ?? '') !== trimmed;
  const err = create.error instanceof Error ? create.error.message : null;

  const check = () => {
    if (!trimmed) return;
    create.reset();
    create.mutate({ name: trimmed, primary_domain: domain.trim() || null },
      { onSuccess: (out) => setChecked(out) });
  };

  const confirm = () => {
    if (!trimmed) return;
    create.reset();
    create.mutate(
      { name: trimmed, primary_domain: domain.trim() || null, confirm_name: trimmed },
      {
        onSuccess: (out) => {
          if (out.created && out.org_id) {
            onCreated({ id: out.org_id, name: out.primary_name || trimmed });
          }
        },
      });
  };

  const matches = checked?.near_matches ?? [];
  const conflicts = checked?.domain_conflicts ?? [];

  return (
    <div className={styles.orgCreate}>
      <div className={styles.orgCreateTitle}>Create a company</div>
      <label className={styles.orgCreateLabel}>
        Name
        <input className={styles.refEditorSearch} autoFocus value={name}
               onChange={(e) => setName(e.target.value)} />
      </label>
      <label className={styles.orgCreateLabel}>
        Web domain (optional)
        <input className={styles.refEditorSearch} value={domain} placeholder="example.com"
               onChange={(e) => setDomain(e.target.value)} />
      </label>
      {/* The cost of leaving the domain blank, said BEFORE the create rather than in the result: mail
          from the company will build a second record beside this one. */}
      {!domain.trim() && (
        <div className={styles.orgCreateNote}>
          With no domain, email from this company will create a second record rather than landing
          here, and it cannot be enriched.
        </div>
      )}

      {checked && !stale && (
        <div className={styles.orgCreateMatches}>
          {matches.length === 0 && conflicts.length === 0 && (
            <span className={styles.gridEmpty}>Nothing else looks like this company.</span>
          )}
          {matches.length > 0 && (
            <>
              <div className={styles.orgCreateMatchHead}>Already in the CRM — is it one of these?</div>
              {matches.map((mm) => {
                const note = stateNote(mm);
                return (
                  <div key={mm.org_id} className={styles.orgCreateMatch}>
                    <span className={styles.orgCreateMatchName}>
                      {mm.primary_name || `#${mm.org_id}`}
                    </span>
                    <span className={styles.refEditorDetail}>
                      {' '}#{mm.org_id} · {BASIS_LABEL[mm.basis]}
                      {mm.primary_domain ? ` · ${mm.primary_domain}` : ''}
                      {note ? ` · ${note}` : ''}
                    </span>
                  </div>
                );
              })}
            </>
          )}
          {/* A claimed domain is a refusal, not a warning — say so before the operator presses on,
              and say WHERE the other record keeps it. Only a primary-slot collision is stopped by
              the unique index; an alt or alias collision is refused because two companies claiming
              one domain is the duplicate this flow exists to prevent. Either way the operator cannot
              act on "taken" without knowing which field to go and edit. */}
          {conflicts.map((c) => (
            <div key={`${c.domain}:${c.org_id}`} className={styles.orgCreateConflict}>
              {c.domain} already belongs to {c.primary_name || `#${c.org_id}`} (#{c.org_id})
              {' '}{SLOT_PHRASE[c.holder_slot] ?? 'as one of its domains'}
              {c.state === 'merged_away'
                ? `, which was merged into #${c.merged_into_id ?? '?'}`
                : c.state === 'soft_deleted' ? ', a deleted record that still holds it' : ''}
              . Clear the domain, or use that record.
            </div>
          ))}
        </div>
      )}
      {stale && (
        <div className={styles.orgCreateNote}>
          You changed the details — check again before creating.
        </div>
      )}
      {err && <div className={styles.orgCreateConflict}>{err}</div>}

      <div className={styles.refEditorFooter}>
        <button className={styles.refEditorClose} onClick={onCancel}>Cancel</button>
        <button className={styles.gridPageBtn} disabled={!trimmed || create.isPending}
                onClick={check}>
          {checked && !stale ? 'Check again' : 'Check for duplicates'}
        </button>
        {/* Step 2 exists only once step 1 has run for THESE details, and never while a domain is
            claimed — the button cannot be the first thing clicked. */}
        {checked && !stale && conflicts.length === 0 && (
          <button className={styles.orgCreateConfirm} disabled={create.isPending} onClick={confirm}>
            Yes — create it
          </button>
        )}
      </div>
    </div>
  );
}
